import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../api/axios';
import { Link } from 'react-scroll';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Dropdown, notification, Spin, Modal, Descriptions } from 'antd';
import CustomNavbar from '../components/Navbar';
import SendMessage from './SendMessage';
import ReportModal from '../components/ReportModal';
import { useProfile } from '../context/ProfileContext';


import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { PrinterOutlined, MoreOutlined, CheckCircleOutlined, HeartFilled, LoadingOutlined, ExportOutlined, MessageOutlined, EditOutlined, CrownFilled, SafetyCertificateOutlined, EyeOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';

const Profile = ({ viewProfileId }) => {
	const navigate = useNavigate();
	const [isSendingInterest, setIsSendingInterest] = useState(false);
	const [isAddingToShortlist, setIsAddingToShortlist] = useState(false);
	const [isSendMessageModalVisible, setIsSendMessageModalVisible] = useState(false);
	const [isReportModalVisible, setIsReportModalVisible] = useState(false);
	const [profileData, setProfileData] = useState(null);
	const [profileError, setProfileError] = useState(null);
	const [hasAcceptedInvite, setHasAcceptedInvite] = useState(false);
	const [myExpectations, setMyExpectations] = useState(null);
	const [isBlocked, setIsBlocked] = useState(false);
	const [blockId, setBlockId] = useState(null);
	const [contactInfo, setContactInfo] = useState(null);
	const [contactLoading, setContactLoading] = useState(false);
	const [contactModalVisible, setContactModalVisible] = useState(false);
	const [copied, setCopied] = useState(false);
	const [upgradeModal, setUpgradeModal] = useState({ visible: false, title: '', description: '' });
	const basicInfoRef = useRef(null);
	const educationInfoRef = useRef(null);
	const socioReligiousInfoRef = useRef(null);
	const physicalStatusInfoRef = useRef(null);
	const familyInfoRef = useRef(null);
	const contactInfoRef = useRef(null);
	const verificationsInfoRef = useRef(null);
	const expectationsInfoRef = useRef(null);
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const profileId = viewProfileId || params.get('id');

	const { profileId: loggedInProfileId } = useProfile();
	const isOwnProfile = profileId === loggedInProfileId;

	useEffect(() => {
		const fetchProfileData = async () => {
			if (!profileId) return;
			if (!isOwnProfile && !loggedInProfileId) return;
			try {
				const apiUrl = isOwnProfile
					? `/profiles/fetch/${profileId}`
					: `/profiles/view/${profileId}`;
				const response = isOwnProfile
					? await api.get(apiUrl)
					: await api.post(apiUrl, { profile_id: loggedInProfileId });
				if (response.data.status === 'success') {
					setProfileData(response.data.data);
				}
			} catch (error) {
				const msg = error.response?.data?.message;
				setProfileError(typeof msg === 'string' ? msg : 'This profile could not be loaded.');
			}
		};

		fetchProfileData();
	}, [profileId, isOwnProfile, loggedInProfileId]);

	useEffect(() => {
		if (isOwnProfile || !loggedInProfileId) return;
		api.get('/expectations/list', { params: { profile_id: loggedInProfileId } })
			.then(res => { if (res.data?.data) setMyExpectations(res.data.data); })
			.catch(() => {});
	}, [loggedInProfileId, isOwnProfile]);

	useEffect(() => {
		if (isOwnProfile || !loggedInProfileId || !profileId) return;
		api.get('/invites/status', { params: { profile_id: loggedInProfileId, other_profile_id: profileId } })
			.then(res => { if (res.data?.data) setHasAcceptedInvite(res.data.data.has_accepted_invite); })
			.catch(() => {});
	}, [loggedInProfileId, profileId, isOwnProfile]);


	useEffect(() => {
		if (isOwnProfile || !loggedInProfileId || !profileId) return;
		api.get('/blocked-profiles/check', { params: { blocker_id: loggedInProfileId, blocked_id: profileId } })
			.then(res => {
				if (res.data?.data) {
					setIsBlocked(res.data.data.is_blocked);
					setBlockId(res.data.data.block_id);
				}
			})
			.catch(() => {});
	}, [loggedInProfileId, profileId, isOwnProfile]);

	if (!profileData) {
		return (
			<div style={{ fontFamily: 'Calibri, sans-serif' }}>
				<Helmet><title>View Profile | Oottan</title></Helmet>
				{!viewProfileId && <CustomNavbar />}
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
					{profileError
						? <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
							<p style={{ fontSize: '1.1rem', marginBottom: 0 }}>{profileError}</p>
						  </div>
						: <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
					}
				</div>
			</div>
		);
	}

	const pictures = profileData.profile_pictures ?? [];
	const images = pictures.length > 0
		? pictures.map(picture => ({
			original: `${process.env.REACT_APP_IMAGE_URL}/${picture.image_path}`,
			thumbnail: `${process.env.REACT_APP_IMAGE_URL}/${picture.image_path}`,
		}))
		: [{
			original: profileData.gender?.toLowerCase() === 'female' ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE : process.env.REACT_APP_MALE_FALLBACK_IMAGE,
			thumbnail: profileData.gender?.toLowerCase() === 'female' ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE : process.env.REACT_APP_MALE_FALLBACK_IMAGE,
		}];

	const handleBlockProfile = async () => {
		try {
			const res = await api.post('/blocked-profiles/block', {
				blocker_id: loggedInProfileId,
				blocked_id: profileId,
			});
			if (res.data.status === 'success') {
				setIsBlocked(true);
				setBlockId(res.data.data?.id ?? null);
				notification.success({ message: 'Profile blocked.' });
			}
		} catch {
			notification.error({ message: 'Failed to block profile.' });
		}
	};

	const handleUnblockProfile = async () => {
		if (!blockId) return;
		try {
			const res = await api.delete(`/blocked-profiles/unblock/${blockId}`, {
				data: { blocker_id: loggedInProfileId },
			});
			if (res.data.status === 'success') {
				setIsBlocked(false);
				setBlockId(null);
				notification.success({ message: 'Profile unblocked.' });
			}
		} catch {
			notification.error({ message: 'Failed to unblock profile.' });
		}
	};

	const handleMenuClick = (e) => {
		switch (e.key) {
			case '2':
				isBlocked ? handleUnblockProfile() : handleBlockProfile();
				break;
			case '3':
				setIsReportModalVisible(true);
				break;
			default:
				break;
		}
	};

	const menuItems = [
		{
			key: '2',
			label: isBlocked ? 'Unblock Profile' : 'Block Profile',
			danger: !isBlocked,
		},
		{
			key: '3',
			label: 'Report Profile/Photo',
		},
	];

	const handlePrint = () => {
		window.print();
	};

	const handleNewTab = () => {
		window.open(`/profile?id=${profileId}`, '_blank');
	};

	const handleSendInterest = async () => {
		setIsSendingInterest(true);
		try {
			const payload = {
				sender_id: loggedInProfileId,
				receiver_id: profileId
			};

			const response = await api.post('/invites/send', payload);

			if (response.data.status === 'success') {
				notification.success({
					message: 'Interest sent!',
					description: 'You have successfully sent interest.',
				});
			} else {
				notification.error({
					message: 'Failed to send interest',
					description: 'Something went wrong. Please try again.',
				});
			}
		} catch (error) {
			notification.error({
				message: 'Failed to send interest',
				description: 'Something went wrong. Please try again.',
			});
		} finally {
			setIsSendingInterest(false);
		}
	};

	const handleAddToShortlist = async () => {
		setIsAddingToShortlist(true);
		try {
			const payload = {
				shortlister_id: loggedInProfileId,
				shortlisted_id: profileId
			};

			const response = await api.post('/shortlists/add', payload);

			if (response.data.status === 'success') {
				notification.success({
					message: 'Added to shortlist!',
					description: 'You have successfully added this profile to your shortlist.',
				});
			} else {
				notification.error({
					message: 'Failed to add to shortlist',
					description: 'Something went wrong. Please try again.',
				});
			}
		} catch (error) {
			notification.error({
				message: 'Failed to add to shortlist',
				description: 'Something went wrong. Please try again.',
			});
		} finally {
			setIsAddingToShortlist(false);
		}
	};

	const handleOpenSendMessageModal = () => {
		setIsSendMessageModalVisible(true);
	};

	const handleCloseSendMessageModal = () => {
		setIsSendMessageModalVisible(false);
	};

	const handleViewContact = async () => {
		if (contactInfo) {
			setContactModalVisible(true);
			return;
		}
		setContactLoading(true);
		try {
			const res = await api.post(`/contact-views/${profileId}`);
			if (res.data.status === 'success') {
				setContactInfo(res.data.data);
				setContactModalVisible(true);
			}
		} catch (error) {
			const code = error.response?.data?.code;
			const msg = error.response?.data?.message;
			if (code === 'no_subscription') {
				setUpgradeModal({
					visible: true,
					title: 'Subscription Required',
					description: 'You need an active subscription to view contact information. Subscribe now to connect with profiles.',
				});
			} else if (code === 'upgrade_required') {
				setUpgradeModal({
					visible: true,
					title: 'Upgrade Your Plan',
					description: 'Your current plan does not include contact views. Upgrade to a higher plan to unlock contact details.',
				});
			} else if (code === 'quota_exhausted') {
				setUpgradeModal({
					visible: true,
					title: 'Contact Views Exhausted',
					description: 'You have used all your contact views for this subscription period. Upgrade your plan to get more contact views.',
				});
			} else if (code === 'rate_limited') {
				notification.warning({
					message: 'Too Many Requests',
					description: 'Please wait a moment before viewing more contact details.',
				});
			} else {
				notification.error({
					message: 'Failed to view contact',
					description: msg || 'Something went wrong. Please try again.',
				});
			}
		} finally {
			setContactLoading(false);
		}
	};

	// ── Verifications ──────────────────────────────────────────────
	const v = profileData.verifications;
	const verifiedItems = v ? [
		v.name_dob_status === 1       && 'Identity',
		v.education_status === 1      && 'Education',
		v.workplace_salary_status === 1 && 'Workplace & Salary',
		v.religion_status === 1       && 'Religion',
		v.caste_status === 1          && 'Caste',
		v.house_status === 1          && 'House Ownership',
		v.car_status === 1            && 'Car Ownership',
	].filter(Boolean) : [];

	// Inline verified badge
	const vBadge = (show) => show ? (
		<span style={{
			display: 'inline-flex', alignItems: 'center', gap: '3px',
			background: '#DCFCE7', color: '#15803D',
			fontSize: '0.68rem', fontWeight: '700',
			padding: '1px 7px', borderRadius: '20px',
			marginLeft: '8px', verticalAlign: 'middle',
			letterSpacing: '0.2px',
		}}>
			<CheckCircleOutlined style={{ fontSize: '9px' }} /> Verified
		</span>
	) : null;

	// ── Preference matches — API returns formatted object, not raw rows ──
	const matches = (() => {
		if (isOwnProfile || !myExpectations) return [];
		const exp = myExpectations;
		const result = [];
		// Age
		if (profileData.date_of_birth && exp.age_range_start && exp.age_range_end) {
			const dob = new Date(profileData.date_of_birth);
			const today = new Date();
			const age = today.getFullYear() - dob.getFullYear() -
				(today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
			if (age >= Number(exp.age_range_start) && age <= Number(exp.age_range_end)) result.push('Age');
		}
		// Religion
		if (exp.religion?.length && profileData.socio_religious_background?.religion &&
			exp.religion.map(r => r.toLowerCase()).includes(profileData.socio_religious_background.religion.toLowerCase()))
			result.push('Religion');
		// Caste
		if (exp.caste?.length && profileData.socio_religious_background?.caste &&
			exp.caste.map(c => c.toLowerCase()).includes(profileData.socio_religious_background.caste.toLowerCase()))
			result.push('Caste');
		// Education
		if (exp.education?.length && profileData.education_occupation?.highest_education) {
			const pe = profileData.education_occupation.highest_education.toLowerCase();
			if (exp.education.some(e => pe.includes(e.toLowerCase()) || e.toLowerCase().includes(pe))) result.push('Education');
		}
		// Occupation
		if (exp.occupation?.length && profileData.education_occupation?.occupation) {
			const po = profileData.education_occupation.occupation.toLowerCase();
			if (exp.occupation.some(o => po.includes(o.toLowerCase()) || o.toLowerCase().includes(po))) result.push('Occupation');
		}
		// State / Location
		if (exp.state?.length && profileData.contact_information?.state &&
			exp.state.map(s => s.toLowerCase()).includes(profileData.contact_information.state.toLowerCase()))
			result.push('Location');
		return result;
	})();

	const cardStyle = {
		marginBottom: '20px',
		borderRadius: '12px',
		border: '1px solid rgba(173,20,87,0.1)',
		boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
		overflow: 'hidden',
	};

	const headStyle = {
		background: 'linear-gradient(135deg, rgba(173,20,87,0.05), rgba(216,27,96,0.03))',
		borderBottom: '1px solid rgba(173,20,87,0.12)',
		fontFamily: 'Calibri, sans-serif',
		fontWeight: '700',
		color: '#1A1A2E',
		fontSize: '1rem',
	};

	return (
		<div style={{ fontFamily: 'Calibri, sans-serif' }}>
			<Helmet>
				<title>
					{profileData
						? `${profileData.first_name} ${profileData.last_name} | Oottan`
						: 'View Profile | Oottan'}
				</title>
			</Helmet>
			{!viewProfileId && <CustomNavbar />}
			<div className={!viewProfileId ? 'dashboard-wrapper' : ''}>
				<div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px 40px' }}>
				<Row gutter={[24, 24]}>
						<Col xs={24} lg={7}>
						<div style={{ position: 'sticky', top: '80px' }}>
							<ImageGallery items={images} showPlayButton={false} />
						</div>
					</Col>
					<Col xs={24} lg={17}>
						{/* Action buttons — top right */}
						<div className="profile-action-buttons" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', paddingTop: '20px' }}>
							{/* Plan badge — left side */}
							{profileData.subscription_plan && profileData.subscription_plan !== 'free' ? (() => {
								const plan = profileData.subscription_plan;
								const isUltimate = plan === 'ultimate';
								return (
									<span style={{
										display: 'inline-flex', alignItems: 'center', gap: '5px',
										background: isUltimate ? 'linear-gradient(135deg,#F59E0B,#FCD34D)' : 'linear-gradient(135deg,#AD1457,#D81B60)',
										borderRadius: '8px', padding: '4px 14px',
										boxShadow: isUltimate ? '0 3px 10px rgba(245,158,11,0.35)' : '0 3px 10px rgba(173,20,87,0.35)',
									}}>
										<CrownFilled style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '13px' }} />
										<span style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.5px' }}>
											{plan.toUpperCase()} MEMBER
										</span>
									</span>
								);
							})() : <span />}
							{/* Buttons — right side */}
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
							{!isOwnProfile && (
								<>
									<Button
										type="primary"
										onClick={handleSendInterest}
										loading={isSendingInterest}
										icon={<HeartFilled />}
										style={{ background: '#10B981', borderColor: '#10B981', borderRadius: '8px', fontWeight: '600' }}
									>
										Send Interest
									</Button>
									<Button
										type="primary"
										onClick={handleAddToShortlist}
										loading={isAddingToShortlist}
										icon={<CheckCircleOutlined />}
										style={{ background: '#F59E0B', borderColor: '#F59E0B', borderRadius: '8px', fontWeight: '600' }}
									>
										Shortlist
									</Button>
									{hasAcceptedInvite && (
										<Button
											type="primary"
											onClick={handleOpenSendMessageModal}
											icon={<MessageOutlined />}
											style={{ background: '#0891B2', borderColor: '#0891B2', borderRadius: '8px', fontWeight: '600' }}
										>
											Message
										</Button>
									)}
									<Button
										type="primary"
										icon={<EyeOutlined />}
										loading={contactLoading}
										onClick={handleViewContact}
										style={{ background: '#7C3AED', borderColor: '#7C3AED', borderRadius: '8px', fontWeight: '600' }}
									>
										View Contact
									</Button>
									<SendMessage
										senderId={loggedInProfileId}
										receiverId={profileId}
										isVisible={isSendMessageModalVisible}
										onClose={handleCloseSendMessageModal}
									/>
								</>
							)}
							{isOwnProfile && (
								<Button
									type="primary"
									icon={<EditOutlined />}
									onClick={() => navigate(`/edit-profile?id=${profileId}`)}
									style={{ background: 'linear-gradient(135deg,#AD1457,#D81B60)', borderColor: '#AD1457', borderRadius: '8px', fontWeight: '600' }}
								>
									Edit Profile
								</Button>
							)}
							{viewProfileId ? (
								<Button
									type="primary"
									icon={<ExportOutlined />}
									onClick={handleNewTab}
									style={{ background: 'linear-gradient(135deg,#AD1457,#D81B60)', borderColor: '#AD1457', borderRadius: '8px', fontWeight: '600' }}
								>
									Open in New Tab
								</Button>
							) : (
								<Button
									type="primary"
									icon={<PrinterOutlined />}
									onClick={handlePrint}
									style={{ background: 'linear-gradient(135deg,#AD1457,#D81B60)', borderColor: '#AD1457', borderRadius: '8px', fontWeight: '600' }}
								>
									Print
								</Button>
							)}
							{!isOwnProfile && (
								<Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
									<Button icon={<MoreOutlined />} style={{ borderRadius: '8px' }} />
								</Dropdown>
							)}
							</div>
						</div>

						{/* ── Profile Highlights: Verifications + Preference Matches ── */}
						{(verifiedItems.length > 0 || matches.length > 0) && (
							<div style={{
								background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F3FF 100%)',
								border: '1px solid #E9D5FF',
								borderRadius: '14px',
								padding: '14px 18px',
								marginBottom: '20px',
								display: 'flex',
								flexDirection: 'column',
								gap: '10px',
							}}>
								{verifiedItems.length > 0 && (
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
										<span style={{
											display: 'inline-flex', alignItems: 'center', gap: '5px',
											color: '#15803D', fontSize: '0.75rem', fontWeight: '700',
											letterSpacing: '0.4px', whiteSpace: 'nowrap',
										}}>
											<SafetyCertificateOutlined /> VERIFIED
										</span>
										{verifiedItems.map(item => (
											<span key={item} style={{
												display: 'inline-flex', alignItems: 'center', gap: '4px',
												background: '#DCFCE7', color: '#15803D',
												fontSize: '0.78rem', fontWeight: '600',
												padding: '3px 11px', borderRadius: '20px',
											}}>
												<CheckCircleOutlined style={{ fontSize: '11px' }} />
												{item}
											</span>
										))}
									</div>
								)}
								{verifiedItems.length > 0 && matches.length > 0 && (
									<div style={{ borderTop: '1px solid #E9D5FF', margin: '0 -2px' }} />
								)}
								{matches.length > 0 && (
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
										<span style={{
											display: 'inline-flex', alignItems: 'center', gap: '5px',
											color: '#AD1457', fontSize: '0.75rem', fontWeight: '700',
											letterSpacing: '0.4px', whiteSpace: 'nowrap',
										}}>
											<HeartFilled style={{ fontSize: '11px' }} /> MATCHES YOUR PREFERENCES
										</span>
										{matches.map(item => (
											<span key={item} style={{
												background: '#FDF2F8', color: '#AD1457',
												fontSize: '0.78rem', fontWeight: '600',
												padding: '3px 11px', borderRadius: '20px',
												border: '1px solid #FBCFE8',
											}}>
												{item}
											</span>
										))}
									</div>
								)}
							</div>
						)}

						{/* Breadcrumb Section */}
						<div className="profile-breadcrumb" style={{ fontFamily: 'Calibri, sans-serif', fontSize: '1.0rem', marginBottom: '16px' }}>
							<Link to="basicInfo" smooth={true} duration={500} style={{ marginRight: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Basic Information
							</Link> |
							<Link to="educationInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Education and Occupation
							</Link> |
							<Link to="socioReligiousInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Socio Religious Background
							</Link> |
							<Link to="physicalStatusInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Physical status and Lifestyle
							</Link> |
							<Link to="familyInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Family Information
							</Link> |
							<Link to="contactInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Contact Information
							</Link> |
							<Link to="verificationsInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Verifications
							</Link> |
							<Link to="expectationsInfo" smooth={true} duration={500} style={{ marginLeft: '10px', color: '#AD1457', cursor: 'pointer', fontWeight: '500' }}>
								Expectations
							</Link>
						</div>
						<Card id="basicInfo" title={<span>Basic Information</span>} style={cardStyle} headStyle={headStyle} ref={basicInfoRef}>
							<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
								<tbody>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Profile ID:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.profile_id}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>First Name:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.first_name}{vBadge(v?.name_dob_status === 1)}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Middle Name:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.middle_name}{profileData.middle_name && vBadge(v?.name_dob_status === 1)}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Last Name:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.last_name}{vBadge(v?.name_dob_status === 1)}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Date of Birth:</td>
										<td style={{ width: '100%' }}>{new Date(profileData.date_of_birth).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}{vBadge(v?.name_dob_status === 1)}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Time of Birth:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.time_of_birth}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Place of Birth:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.place_of_birth}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Gender:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.gender}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Marital Status:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.marital_status}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Current Location:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.current_location}</td>
									</tr>
									<tr>
										<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Profile Created For:</td>
										<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.profile_created_for}</td>
									</tr>
								</tbody>
							</table>
						</Card>
						{profileData.education_occupation && (
							<Card id='educationInfo' title={<span>Education and Occupation</span>} style={cardStyle} headStyle={headStyle} ref={educationInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Education Category:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.education_category}{vBadge(v?.education_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Highest Education:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.highest_education}{vBadge(v?.education_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Education Detail:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.education_detail}{vBadge(v?.education_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Occupation Detail:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.occupation_detail}{vBadge(v?.workplace_salary_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Occupation:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.occupation}{vBadge(v?.workplace_salary_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Annual Income Range:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.annual_income_range}{vBadge(v?.workplace_salary_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Employed In:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.education_occupation.employed_in}{vBadge(v?.workplace_salary_status === 1)}</td>
										</tr>
									</tbody>
								</table>
							</Card>
						)}
						{profileData.socio_religious_background && (
							<Card id='socioReligiousInfo' title={<span>Socio Religious Background</span>} style={cardStyle} headStyle={headStyle} ref={socioReligiousInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Religion:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.religion}{vBadge(v?.religion_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Denomination:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.denomination}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Caste:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.caste}{vBadge(v?.caste_status === 1)}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Subcaste:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.subcaste}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Division:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.division}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Mother Tongue:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.socio_religious_background.mother_tongue}</td>
										</tr>
									</tbody>
								</table>
							</Card>
						)}
						{profileData.physical_lifestyle && (
							<Card id='physicalStatusInfo' title={<span>Physical status and Lifestyle</span>} style={cardStyle} headStyle={headStyle} ref={physicalStatusInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Height:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.height}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Weight:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.weight}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Body Type:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.body_type}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Physical Status:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.physical_status}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Complexion:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.complexion}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Blood Group:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.blood_group}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Diet:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.diet}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Smoke:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.smoke}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Drink:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.physical_lifestyle.drink}</td>
										</tr>
									</tbody>
								</table>
							</Card>
						)}
						{profileData.family_information && (
							<Card id='familyInfo' title={<span>Family Information</span>} style={cardStyle} headStyle={headStyle} ref={familyInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Family Values:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.family_values}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Family Type:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.family_type}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Family Origin:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.family_origin}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Father's Name:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.father_name}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Father Alive:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.father_alive ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Father's Occupation:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.father_occupation}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Mother's Name:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.mother_name}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Mother Alive:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.mother_alive ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Mother's Occupation:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.mother_occupation}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>No. of Brothers:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.no_of_brothers}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>No. of Sisters:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.no_of_sisters}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>No. of Brothers Married:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.no_of_brothers_married}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>No. of Sisters Married:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.family_information.no_of_sisters_married}</td>
										</tr>
									</tbody>
								</table>
							</Card>
						)}
						{profileData.contact_information && (
							<Card
								id='contactInfo'
								title={<span>Contact Information</span>}
								style={cardStyle}
								headStyle={headStyle}
								ref={contactInfoRef}
							>
								{(() => {
									const ci = profileData.contact_information;
									const tdLabel = { whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' };
									const tdValue = { width: '100%', paddingBottom: '10px', color: '#1A1A2E' };
									if (!ci) return null;
									return (
										<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
											<tbody>
												<tr><td style={tdLabel}>Mobile Number:</td><td style={tdValue}>{ci.mobile_number}</td></tr>
												<tr><td style={tdLabel}>Alternative Mobile:</td><td style={tdValue}>{ci.alternative_mobile_number}</td></tr>
												<tr><td style={tdLabel}>WhatsApp Number:</td><td style={tdValue}>{ci.whatsapp_number}</td></tr>
												<tr><td style={tdLabel}>Email Address:</td><td style={tdValue}>{ci.email_address}</td></tr>
												<tr><td style={tdLabel}>Door No:</td><td style={tdValue}>{ci.door_no}</td></tr>
												<tr><td style={tdLabel}>Address 1:</td><td style={tdValue}>{ci.address_1}</td></tr>
												<tr><td style={tdLabel}>Address 2:</td><td style={tdValue}>{ci.address_2}</td></tr>
												<tr><td style={tdLabel}>Landmark:</td><td style={tdValue}>{ci.landmark}</td></tr>
												<tr><td style={tdLabel}>Country:</td><td style={tdValue}>{ci.country}</td></tr>
												<tr><td style={tdLabel}>State:</td><td style={tdValue}>{ci.state}</td></tr>
												<tr><td style={tdLabel}>City:</td><td style={tdValue}>{ci.city}</td></tr>
												<tr><td style={tdLabel}>District:</td><td style={tdValue}>{ci.district}</td></tr>
												<tr><td style={tdLabel}>Postal Code:</td><td style={tdValue}>{ci.postal_code}</td></tr>
												<tr><td style={tdLabel}>Latitude:</td><td style={tdValue}>{ci.latitude}</td></tr>
												<tr><td style={tdLabel}>Longitude:</td><td style={tdValue}>{ci.longitude}</td></tr>
											</tbody>
										</table>
									);
								})()}
							</Card>
						)}
						{profileData.verifications && (
							<Card id='verificationsInfo' title={<span>Verifications</span>} style={cardStyle} headStyle={headStyle} ref={verificationsInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Name Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.name_dob_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>DOB Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.name_dob_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Workplace Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.workplace_salary_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Salary Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.workplace_salary_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>House Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.house_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Car Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.car_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Caste Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.caste_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Religion Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.religion_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
										<tr>
											<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Education Verified:</td>
											<td style={{ width: '100%', paddingBottom: '10px', color: '#1A1A2E' }}>{profileData.verifications.education_status === 1 ? 'Yes' : 'No'}</td>
										</tr>
									</tbody>
								</table>
							</Card>
						)}
						{!isOwnProfile && profileData.expectations && (
							<Card id='expectationsInfo' title={<span>Expectations</span>} style={cardStyle} headStyle={headStyle} ref={expectationsInfoRef}>
								<table style={{ width: '100%', fontFamily: 'Calibri, sans-serif', fontSize: '0.95rem', borderCollapse: 'collapse' }}>
									<tbody>
										{(() => {
											// Helper function to get preference values by type
											const getPreferenceValues = (type) => {
												const preferences = profileData.expectations.filter(exp => exp.preference_type === type);
												return preferences.map(pref => pref.preference_value).join(', ');
											};

											// Helper function to get range values
											const getRangeValues = (type) => {
												const minPref = profileData.expectations.find(exp => exp.preference_type === type && exp.preference_key === 'min');
												const maxPref = profileData.expectations.find(exp => exp.preference_type === type && exp.preference_key === 'max');
												const min = minPref ? minPref.preference_value : '';
												const max = maxPref ? maxPref.preference_value : '';
												return min && max ? `${min}-${max}` : 'Not specified';
											};

											// Helper function to get single preference value
											const getSinglePreference = (type) => {
												const pref = profileData.expectations.find(exp => exp.preference_type === type && exp.preference_key === 'preferred');
												return pref ? pref.preference_value : 'Not specified';
											};

											return (
												<>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Age Range:</td>
														<td style={{ width: '100%' }}>{getRangeValues('age')}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Height Range:</td>
														<td style={{ width: '100%' }}>{getRangeValues('height')}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Weight Range:</td>
														<td style={{ width: '100%' }}>{getRangeValues('weight')}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Religion:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('religion') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Denomination:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('denomination') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Caste:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('caste') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Sub Caste:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('sub_caste') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Education:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('education') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Occupation:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('occupation') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Employed In:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('employed_in') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Salary Range:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('salary_range') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Languages Known:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('languages_known') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Complexion:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('complexion') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Physical Status:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('physical_status') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>City:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('city') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>State:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('state') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Country:</td>
														<td style={{ width: '100%' }}>{getPreferenceValues('country') || 'Not specified'}</td>
													</tr>
													<tr>
														<td style={{ whiteSpace: 'nowrap', paddingRight: '32px', paddingBottom: '10px', color: '#64748B', fontWeight: '500', width: '1%' }}>Looking for:</td>
														<td style={{ width: '100%' }}>{getSinglePreference('looking_for')}</td>
													</tr>
												</>
											);
										})()}
									</tbody>
								</table>
							</Card>
						)}
					</Col>
				</Row>
				</div>
			</div>
			<ReportModal
				isOpen={isReportModalVisible}
				onClose={() => setIsReportModalVisible(false)}
				profileId={profileId}
			/>
			<Modal
				open={contactModalVisible}
				onCancel={() => { setContactModalVisible(false); setCopied(false); }}
				title={
					<span style={{ color: '#AD1457', fontFamily: 'Calibri, sans-serif' }}>
						Contact Details — {profileData.first_name} {profileData.last_name}
					</span>
				}
				footer={
					<Button
						icon={copied ? <CheckOutlined /> : <CopyOutlined />}
						onClick={() => {
							if (!contactInfo) return;
							const fields = [
								['Mobile Number', contactInfo.mobile_number],
								['Alternative Mobile', contactInfo.alternative_mobile_number],
								['WhatsApp Number', contactInfo.whatsapp_number],
								['Email Address', contactInfo.email_address],
								['Door No', contactInfo.door_no],
								['Address 1', contactInfo.address_1],
								['Address 2', contactInfo.address_2],
								['Landmark', contactInfo.landmark],
								['City', contactInfo.city],
								['District', contactInfo.district],
								['State', contactInfo.state],
								['Country', contactInfo.country],
								['Postal Code', contactInfo.postal_code],
							].filter(([, v]) => v).map(([l, v]) => `${l}: ${v}`).join('\n');
							navigator.clipboard.writeText(fields).then(() => {
								setCopied(true);
								setTimeout(() => setCopied(false), 2000);
							});
						}}
						style={copied ? { color: '#10B981', borderColor: '#10B981' } : {}}
					>
						{copied ? 'Copied!' : 'Copy Details'}
					</Button>
				}
				centered
				width={520}
			>
				{contactInfo && (
					<Descriptions column={1} size="small" style={{ marginTop: 8 }} labelStyle={{ color: '#64748B', fontWeight: 500, width: 180 }} contentStyle={{ color: '#1A1A2E' }}>
						{contactInfo.mobile_number             && <Descriptions.Item label="Mobile Number">{contactInfo.mobile_number}</Descriptions.Item>}
						{contactInfo.alternative_mobile_number && <Descriptions.Item label="Alternative Mobile">{contactInfo.alternative_mobile_number}</Descriptions.Item>}
						{contactInfo.whatsapp_number           && <Descriptions.Item label="WhatsApp Number">{contactInfo.whatsapp_number}</Descriptions.Item>}
						{contactInfo.email_address             && <Descriptions.Item label="Email Address">{contactInfo.email_address}</Descriptions.Item>}
						{contactInfo.door_no                   && <Descriptions.Item label="Door No">{contactInfo.door_no}</Descriptions.Item>}
						{contactInfo.address_1                 && <Descriptions.Item label="Address 1">{contactInfo.address_1}</Descriptions.Item>}
						{contactInfo.address_2                 && <Descriptions.Item label="Address 2">{contactInfo.address_2}</Descriptions.Item>}
						{contactInfo.landmark                  && <Descriptions.Item label="Landmark">{contactInfo.landmark}</Descriptions.Item>}
						{contactInfo.city                      && <Descriptions.Item label="City">{contactInfo.city}</Descriptions.Item>}
						{contactInfo.district                  && <Descriptions.Item label="District">{contactInfo.district}</Descriptions.Item>}
						{contactInfo.state                     && <Descriptions.Item label="State">{contactInfo.state}</Descriptions.Item>}
						{contactInfo.country                   && <Descriptions.Item label="Country">{contactInfo.country}</Descriptions.Item>}
						{contactInfo.postal_code               && <Descriptions.Item label="Postal Code">{contactInfo.postal_code}</Descriptions.Item>}
					</Descriptions>
				)}
			</Modal>
			<Modal
				open={upgradeModal.visible}
				onCancel={() => setUpgradeModal(m => ({ ...m, visible: false }))}
				footer={null}
				centered
				width={420}
			>
				<div style={{ textAlign: 'center', padding: '16px 8px 8px' }}>
					<CrownFilled style={{ fontSize: 48, color: '#F59E0B', marginBottom: 16 }} />
					<div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A1A2E', marginBottom: 10 }}>
						{upgradeModal.title}
					</div>
					<div style={{ fontSize: '0.92rem', color: '#64748B', marginBottom: 24, lineHeight: 1.6 }}>
						{upgradeModal.description}
					</div>
					<Button
						type="primary"
						size="large"
						icon={<CrownFilled />}
						onClick={() => { setUpgradeModal(m => ({ ...m, visible: false })); navigate('/pricing'); }}
						style={{ background: 'linear-gradient(135deg,#F59E0B,#D97706)', borderColor: '#D97706', fontWeight: 700, borderRadius: 8, width: '100%' }}
					>
						View Plans
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export default Profile;